import { NavigateFunction } from 'react-router-dom';
import { normalizeSlug, isValidSlug } from './routeConfig';

/**
 * Navigate to auth while intelligently preserving organization slug.
 * 
 * Fallback priority:
 * 1. Current URL path (/:slug)
 * 2. Query parameter (?slug=)
 * 3. sessionStorage org_context
 * 4. sessionStorage last_slug_path
 * 5. Global __BOOTSTRAP_SLUG__
 * 6. Root (/) - NEVER /auth to avoid loops
 */
export function navigateToAuthPreservingSlug(navigate: NavigateFunction) {

  // 1) Path: "/:slug" eller "/:slug/"
  const pathMatch = window.location.pathname
    .replace(/\/+$/, '')
    .match(/^\/([a-z0-9-]+)$/);
    
  if (pathMatch && isValidSlug(pathMatch[1])) {
    const slug = normalizeSlug(pathMatch[1]);
    console.log('[navigateToAuth] Preserving slug from path:', slug);
    navigate(`/${slug}`, { replace: true });
    return;
  }

  // 2) Query: ?slug=...
  const sp = new URLSearchParams(window.location.search);
  const querySlugRaw = sp.get('slug');
  const querySlug = querySlugRaw ? normalizeSlug(querySlugRaw) : '';
  
  if (querySlug && isValidSlug(querySlug)) {
    console.log('[navigateToAuth] Preserving slug from query:', querySlug);
    navigate(`/${querySlug}`, { replace: true });
    return;
  }

  // 3) sessionStorage org_context
  try {
    const raw = sessionStorage.getItem('org_context');
    if (raw) {
      const ctx = JSON.parse(raw);
      const savedSlug = normalizeSlug(String(ctx?.org_slug || ''));
      
      if (savedSlug && isValidSlug(savedSlug)) {
        console.log('[navigateToAuth] Preserving slug from session:', savedSlug);
        navigate(`/${savedSlug}`, { replace: true });
        return;
      }
    }
  } catch (e) {
    console.warn('[navigateToAuth] Failed reading org_context:', e);
  }

  // 4) last_slug_path (global guard from bootstrap/AppRoutes)
  const lastSlugRaw = sessionStorage.getItem('last_slug_path') || '';
  const lastSlug = normalizeSlug(lastSlugRaw);
  
  if (lastSlug && isValidSlug(lastSlug)) {
    console.log('[navigateToAuth] Preserving slug from last_slug_path:', lastSlug);
    navigate(`/${lastSlug}`, { replace: true });
    return;
  }

  // 5) Global variable from bootstrap (ultimate fallback)
  const globalSlugRaw = (window as any).__BOOTSTRAP_SLUG__ || '';
  const globalSlug = normalizeSlug(globalSlugRaw);
  if (globalSlug && isValidSlug(globalSlug)) {
    console.log('[navigateToAuth] Preserving slug from global:', globalSlug);
    navigate(`/${globalSlug}`, { replace: true });
    return;
  }

  // 6) Fallback: Root (AuthPage canonical bounce tar vid)
  console.log('[navigateToAuth] No slug found → fallback to /');
  navigate('/', { replace: true });
}
